import React, { useState, useEffect } from "react";
import { FileText, Clock, CheckCircle, UserCheck, Send, X, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { fetchReviewerQueue, respondToInvitation } from "../service/ReviewerService";

const statusConfig = {
    invitationSent: { label: "Pending Acceptance", cls: "badge-pr-awaiting" },
    inReview:       { label: "In Review",          cls: "badge-pr-inreview" },
    reviewCompleted:{ label: "Completed",          cls: "badge-pr-completed" },
};

export default function ReviewerDashboard() {
    const navigate = useNavigate();
    const [pending, setPending] = useState([]);
    const [active, setActive] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [loading, setLoading] = useState(true);
    const [authorized, setAuthorized] = useState(true);
    const [activeTab, setActiveTab] = useState("pending");
    const [responding, setResponding] = useState(null);

    const loadQueue = () => {
        fetchReviewerQueue()
            .then(res => {
                if (res.authorized === false) { setAuthorized(false); return; }
                setPending(res.pending ?? []);
                setActive(res.active ?? []);
                setCompleted(res.completed ?? []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadQueue(); }, []);

    const handleRespond = async (peerReviewId, action) => {
        if (action === "decline" && !window.confirm("Are you sure you want to decline this review?")) return;
        setResponding(peerReviewId);
        try {
            await respondToInvitation(peerReviewId, action, null);
            loadQueue();
        } catch {}
        finally { setResponding(null); }
    };

    const tabs = [
        { key: "pending", label: "Pending", count: pending.length },
        { key: "active", label: "Active", count: active.length },
        { key: "completed", label: "Completed", count: completed.length },
    ];

    const currentList = activeTab === "pending" ? pending : activeTab === "active" ? active : completed;

    if (!authorized) {
        return (
            <div className="home-layout">
                <Sidebar activePage="Review Queue" />
                <main className="home-main">
                    <Header />
                    <div className="home-content">
                        <div className="upload-main">
                            <div className="submissions-empty">
                                <UserCheck size={40} className="submissions-empty-icon" />
                                <p className="submissions-empty-title">Reviewer Access Required</p>
                                <p className="submissions-empty-sub">You are not currently registered as a reviewer.</p>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="home-layout">
            <Sidebar activePage="Review Queue" />

            <main className="home-main">
                <Header />

                <div className="home-content">
                    <div className="upload-main">
                        <div className="upload-page-title-row">
                            <h1 className="upload-page-heading">Review Queue</h1>
                        </div>

                        {/* Tabs */}
                        <div className="pr-tabs">
                            {tabs.map(t => (
                                <button
                                    key={t.key}
                                    className={`pr-tab${activeTab === t.key ? " pr-tab-active" : ""}`}
                                    onClick={() => setActiveTab(t.key)}
                                >
                                    {t.label}
                                    {t.count > 0 && <span className="pr-tab-count">{t.count}</span>}
                                </button>
                            ))}
                        </div>

                        {/* List */}
                        <div className="sub-list">
                            {loading ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i} className="sub-card sub-card-skeleton">
                                        <div className="skeleton skeleton-icon" />
                                        <div className="sub-info">
                                            <div className="skeleton skeleton-title" />
                                            <div className="skeleton skeleton-authors" />
                                        </div>
                                        <div className="sub-meta">
                                            <div className="skeleton skeleton-date" />
                                        </div>
                                    </div>
                                ))
                            ) : currentList.length === 0 ? (
                                <div className="submissions-empty">
                                    <FileText size={32} className="submissions-empty-icon" />
                                    <p className="submissions-empty-title">
                                        {activeTab === "pending" ? "No pending invitations" : activeTab === "active" ? "No active reviews" : "No completed reviews"}
                                    </p>
                                </div>
                            ) : currentList.map(review => (
                                <div key={review.id} className="sub-card" style={{ cursor: activeTab === "active" ? "pointer" : "default" }}
                                     onClick={() => activeTab === "active" && navigate(`/reviewer/review/${review.id}`)}>
                                    <div className="sub-icon">
                                        <FileText size={15} />
                                    </div>
                                    <div className="sub-info">
                                        <p className="sub-title">{review.manuscriptTitle || "Untitled"}</p>
                                        <p className="sub-authors">{review.manuscriptAuthors || "Unknown Authors"}</p>
                                        {review.notes && <p className="pr-notes-display" style={{ marginTop: 4 }}>Author notes: {review.notes}</p>}
                                        {review.reviewDeadline && activeTab === "active" && (
                                            <p style={{ fontSize: 11, color: "var(--ink-400)", marginTop: 4 }}>
                                                Due {new Date(review.reviewDeadline).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                            </p>
                                        )}
                                    </div>
                                    <div className="sub-meta">
                                        <p className="sub-date">
                                            {review.submittedDate ? new Date(review.submittedDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                                        </p>
                                        <div className="sub-right" style={{ gap: 6 }}>
                                            {activeTab === "pending" && (
                                                <>
                                                    <button
                                                        className="pr-submit-btn"
                                                        style={{ fontSize: 11, padding: "5px 10px" }}
                                                        disabled={responding === review.id}
                                                        onClick={(e) => { e.stopPropagation(); handleRespond(review.id, "accept"); }}
                                                    >
                                                        <CheckCircle size={11} />
                                                        {responding === review.id ? "..." : "Accept"}
                                                    </button>
                                                    <button
                                                        className="pr-cancel-btn"
                                                        disabled={responding === review.id}
                                                        onClick={(e) => { e.stopPropagation(); handleRespond(review.id, "decline"); }}
                                                        title="Decline"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </>
                                            )}
                                            {activeTab === "active" && (
                                                <button
                                                    className="pr-submit-btn"
                                                    style={{ fontSize: 11, padding: "5px 10px" }}
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/reviewer/review/${review.id}`); }}
                                                >
                                                    <Send size={11} />
                                                    Write Review
                                                </button>
                                            )}
                                            {activeTab === "completed" && (
                                                <span className={`status-badge badge-pr-completed`}>
                                                    <CheckCircle size={12} />
                                                    Completed
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
